import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ContributeForm } from "@/components/ContributeForm";
import { ContributorContributionsSection } from "@jl/components/ContributorContributionsSection";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { PenTool, CheckCircle, Clock, Shield, Sparkles } from "lucide-react";
import logo from "@/assets/logo.png";

const Contribute = () => {
  const { t } = useLanguage();
  const { contributorUser, isContributorAuthenticated } = useAuth();
  const location = useLocation();
  const [formOpen, setFormOpen] = useState(false);
  const [contributionsRefreshKey, setContributionsRefreshKey] = useState(0);

  useEffect(() => {
    if (location.hash === "#contributions") {
      const id = requestAnimationFrame(() => {
        document
          .getElementById("contributions")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
      return () => cancelAnimationFrame(id);
    }
    return undefined;
  }, [location.pathname, location.hash]);

  const bumpContributionsRefresh = () => {
    setContributionsRefreshKey(k => k + 1);
  };

  const guidelines = [
    {
      icon: CheckCircle,
      titleKey: "contribute.g1Title",
      descKey: "contribute.g1Desc",
    },
    {
      icon: PenTool,
      titleKey: "contribute.g2Title",
      descKey: "contribute.g2Desc",
    },
    {
      icon: Clock,
      titleKey: "contribute.g3Title",
      descKey: "contribute.g3Desc",
    },
    {
      icon: Shield,
      titleKey: "contribute.g4Title",
      descKey: "contribute.g4Desc",
    },
  ] as const;

  return (
    <div className="min-h-screen">
      {/* Hero Header */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[hsl(220,40%,13%)] via-[hsl(220,38%,18%)] to-[hsl(218,35%,22%)]">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="bg-primary/20 float absolute -right-20 -top-20 h-60 w-60 rounded-full blur-3xl" />
          <div className="bg-gold/15 float-delayed absolute -bottom-20 -left-20 h-80 w-80 rounded-full blur-3xl" />
        </div>

        {/* Decorative Pattern */}
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 50'%3E%3Ccircle cx='10' cy='10' r='1' fill='%23fff'/%3E%3Ccircle cx='30' cy='15' r='1' fill='%23fff'/%3E%3Ccircle cx='50' cy='8' r='1' fill='%23fff'/%3E%3Ccircle cx='70' cy='20' r='1' fill='%23fff'/%3E%3Ccircle cx='90' cy='12' r='1' fill='%23fff'/%3E%3C/svg%3E")`,
            backgroundSize: "150px 75px",
          }}
        />

        <div className="container relative py-5 md:py-12">
          <div className="flex flex-col items-center text-center">
            <div className="animate-fade-in-up relative mb-6">
              <div className="bg-primary/20 absolute inset-0 scale-150 rounded-full blur-xl" />
              <img
                src={logo}
                alt="Jain Lexicon"
                className="relative h-16 w-auto object-contain"
              />
            </div>
            <div
              className="animate-fade-in-up mb-6 flex h-16 w-16 items-center justify-center rounded-2xl border border-white/20 bg-white/10 backdrop-blur-sm"
              style={{ animationDelay: "0.1s" }}
            >
              <PenTool className="text-primary h-8 w-8" />
            </div>
            <h1
              className="animate-fade-in-up mb-4 font-serif text-3xl font-bold text-white md:text-5xl"
              style={{ animationDelay: "0.2s" }}
            >
              {t("contribute.title")}
            </h1>
            <p
              className="animate-fade-in-up max-w-xl text-base text-white/70 md:text-lg"
              style={{ animationDelay: "0.3s" }}
            >
              {t("contribute.description")}
            </p>
          </div>
        </div>

        {/* Bottom Wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg
            viewBox="0 0 1440 40"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full"
          >
            <path
              d="M0 40V20C360 35 720 5 1080 20C1260 27.5 1350 32.5 1440 35V40H0Z"
              fill="white"
            />
          </svg>
        </div>
      </section>

      {/* Guidelines */}
      <section className="bg-background py-8 md:py-9">
        <div className="container">
          <div className="mb-5 text-center md:mb-6">
            <div className="bg-primary/10 text-primary mb-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium md:text-sm md:px-3.5 md:py-2">
              <Sparkles className="h-3.5 w-3.5 md:h-4 md:w-4" />
              {t("contribute.guidelinesBadge")}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4">
            {guidelines.map(item => (
              <div
                key={item.titleKey}
                className="from-secondary/30 border-border/50 hover:border-primary/30 group rounded-xl border bg-gradient-to-b to-transparent p-3.5 text-center transition-all hover:shadow-md sm:p-4 md:p-4"
              >
                <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-white shadow-sm transition-transform group-hover:scale-105 sm:h-11 sm:w-11">
                  <item.icon className="text-primary h-5 w-5" />
                </div>
                <h3 className="text-foreground mb-1 text-sm font-semibold leading-tight sm:mb-1.5 md:text-base">
                  {t(item.titleKey)}
                </h3>
                <p className="text-muted-foreground text-xs leading-snug sm:text-sm sm:leading-relaxed">
                  {t(item.descKey)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contributions + submit */}
      <section className="from-secondary/20 to-background relative overflow-hidden bg-gradient-to-b py-5 md:py-16">
        {/* Background Decoration */}
        <div className="bg-primary/5 absolute right-0 top-0 h-96 w-96 -translate-y-1/2 translate-x-1/2 rounded-full blur-3xl" />
        <div className="bg-gold/5 absolute bottom-0 left-0 h-96 w-96 -translate-x-1/2 translate-y-1/2 rounded-full blur-3xl" />

        <div className="container relative mx-auto max-w-6xl space-y-10 px-4">
          <div
            id="contributions"
            className="scroll-mt-24 flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between"
          >
            <div className="min-w-0 flex-1 text-center sm:text-left">
              <h2 className="text-foreground font-serif text-2xl font-bold md:text-3xl">
                {t("contribute.myContributions")}
              </h2>
              <p className="text-muted-foreground mt-2 text-sm">
                {t("contribute.trackStatus")}
              </p>
              {isContributorAuthenticated && contributorUser?.email ? (
                <p className="text-muted-foreground mt-2 text-xs">
                  {t("contribute.showingFor")}{" "}
                  <span className="text-foreground font-medium">
                    {contributorUser.email}
                  </span>
                </p>
              ) : null}
            </div>
            <Button
              type="button"
              size="lg"
              onClick={() => setFormOpen(true)}
              className="bg-primary hover:bg-primary/90 text-primary-foreground btn-shimmer h-12 shrink-0 px-8 text-base max-sm:w-full sm:self-start"
            >
              <PenTool className="mr-2 h-5 w-5" />
              {t("contribute.submitNew")}
            </Button>
          </div>

          <ContributorContributionsSection
            refreshKey={contributionsRefreshKey}
            onOpenSubmitForm={() => setFormOpen(true)}
          />

          <Dialog open={formOpen} onOpenChange={setFormOpen}>
            <DialogContent className="max-h-[min(92vh,900px)] w-full max-w-2xl translate-y-[-48%] gap-4 overflow-y-auto p-6 sm:p-8">
              <ContributeForm
                variant="dialog"
                onContributionSuccess={() => {
                  setFormOpen(false);
                  bumpContributionsRefresh();
                }}
              />
            </DialogContent>
          </Dialog>
        </div>
      </section>
    </div>
  );
};

export default Contribute;
